import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';

import { NotificationService } from '../../../services/notification.service';
import { AuthService } from '../../../services/auth.service';
import { LearningSuccessControlService } from '../../../services/learning-success-control.service';

import { Course } from '../../../models/course.model';
import { user } from '../../../models/user.model';
import { LearningSuccessControl } from '../../../models/learningSuccessControl.model';

import { FiledropComponent } from '../../filedrop/filedrop.component';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { ActivityService } from '../../../services/activity.service';
import { UserService } from '../../../services/user.service';
import { InputNumberModule } from 'primeng/inputnumber';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DialogManagerService } from 'src/app/services/dialog-manager.service';
import { CourseService } from '../../../services/course.service';
@Component({
  selector: 'app-create-learnsuccesscontrol-dialog',
  standalone: true,
  imports: [
    DropdownModule,
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    CommonModule,
    FiledropComponent,
    FloatLabelModule,
    InputTextModule,
    InputNumberModule,
  ],
  templateUrl: './create-learnsuccesscontrol-dialog.component.html',
  styleUrl: './create-learnsuccesscontrol-dialog.component.scss',
})
export class CreateLearnsuccesscontrolDialogComponent implements OnInit {
  @ViewChild(FiledropComponent) fileDropComponent!: FiledropComponent;

  @Input() dialogVisible: boolean = false;
  @Output() dialogVisibleChange = new EventEmitter<boolean>();

  availableParticipants: user[] = [];
  availableCourses: Course[] = [];
  courses: Course[] = [];
  isSubmitting: boolean = false;

  learnsuccesscontrolForm: FormGroup;

  selectedUser: user | null = null;

  constructor(
    private authService: AuthService,
    private learningSuccessControlService: LearningSuccessControlService,
    private notificationService: NotificationService,
    private activityService: ActivityService,
    private userService: UserService,
    private fb: FormBuilder,
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private dialogManager: DialogManagerService,
    private courseService: CourseService
  ) {
    this.learnsuccesscontrolForm = this.fb.group({
      title: ['', Validators.required],
      courseId: [null, Validators.required],
      participantId: [null, Validators.required],
      gesamtnote: [
        null,
        [Validators.required, Validators.min(1), Validators.max(6)],
      ],
      gesamtpunkte: [
        null,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
    });
  }

  ngOnInit(): void {
    this.selectedUser = this.config.data?.selectedUser ?? null;

    const interessentKurs: Course = {
      courseId: -1,
      courseName: '--- Interessent:innen (ohne Kurs) ---',
      users: [],
      startDate: '',
      locationId: 0,
      courseTypeId: 0,
      isActive: true,
    };

    if (this.selectedUser) {
      this.learnsuccesscontrolForm
        .get('participantId')
        ?.setValue(this.selectedUser.userId);
      this.learnsuccesscontrolForm.get('participantId')?.disable();
      this.learnsuccesscontrolForm.get('participantId')?.clearValidators();
      this.learnsuccesscontrolForm
        .get('participantId')
        ?.updateValueAndValidity();
      const isInteressentOhneKurs =
        this.selectedUser.isLead &&
        (!this.selectedUser.courses || this.selectedUser.courses.length === 0);

      if (isInteressentOhneKurs) {
        this.learnsuccesscontrolForm.get('courseId')?.setValue(-1);
        this.learnsuccesscontrolForm.get('courseId')?.disable();
        this.learnsuccesscontrolForm.get('courseId')?.updateValueAndValidity();
        this.courses = [interessentKurs];
      } else {
        this.courseService.getCourseObservable().subscribe({
          next: (courses) => {
            this.courses = [interessentKurs, ...courses];

            const userCourse = courses.find((c) =>
              c.users.some((u) => u.userId === this.selectedUser?.userId)
            );
            if (userCourse) {
              this.learnsuccesscontrolForm
                .get('courseId')
                ?.setValue(userCourse.courseId);
              this.learnsuccesscontrolForm.get('courseId')?.disable();
              this.learnsuccesscontrolForm
                .get('courseId')
                ?.updateValueAndValidity();
            }
          },
          error: () => console.error('Kurse nicht geladen!'),
        });
      }
    } else {
      this.loadUserCourses();
    }
  }

  loadUserCourses() {
    const userId = this.authService.getCurrentUserId();

    this.userService.getUserCoursesObservable(userId).subscribe({
      next: (courses) => {
        const interessentKurs: Course = {
          courseId: -1,
          courseName: '--- Interessent:innen (ohne Kurs) ---',
          users: [],
          startDate: '',
          locationId: 0,
          courseTypeId: 0,
          isActive: true,
        };
        this.availableCourses = [interessentKurs, ...courses];
      },
      error: (err) => {
        console.error('Fehler beim Laden der Kurse', err);
      },
    });
  }

  closeDialog() {
    this.ref.close();
    this.dialogManager.closeLearningDialog();
    this.learnsuccesscontrolForm.reset();
    this.dialogVisible = false;
    this.dialogVisibleChange.emit(this.dialogVisible);
  }

  onCourseSelected(courseId: number) {
    const currentCourse: Course = this.availableCourses.find(
      (c) => c.courseId == courseId
    );
    this.availableParticipants = currentCourse
      ? currentCourse.users.filter((u) => u.roleIds.includes(1))
      : [];
  }

  onFileUploadStarted() {
    this.isSubmitting = true;
  }

  onFilesUploaded(fileUrls: string[]) {
    const rawForm = this.learnsuccesscontrolForm.getRawValue();
    const courseId = rawForm.courseId;
    const participantId = rawForm.participantId;

    const formValues: LearningSuccessControl = {
      gesamtnote: this.learnsuccesscontrolForm.value.gesamtnote,
      gesamtpunkte: this.learnsuccesscontrolForm.value.gesamtpunkte,
      courseIds: courseId == null ? [] : [courseId],
      userIds: participantId == null ? [] : [participantId],
      creatorId: this.authService.getCurrentUserId(),
      title: this.learnsuccesscontrolForm.value.title,
      fileUrls: fileUrls,
    };
    this.learningSuccessControlService.saveLearning(formValues).subscribe({
      next: (activity) => {
        this.closeDialog();
        this.activityService.addActivity(activity);
        this.notificationService.success(
          `Aktivität "Lernerfolgskontrolle" erfolgreich erstellt`,
          activity
        );
      },
      error: () => {
        this.notificationService.error(
          `Aktivitat "Lernerfolgskontrolle" NICHT erstellt`
        );
      },
    });

    this.isSubmitting = false;
  }

  learningDialogSubmit(): void {
    this.isSubmitting = true;
    this.fileDropComponent.requestUpload();
  }
}
